import React from "react";
import { ApiError, cockpit } from "@/api";
import type { RequestActivityPage } from "@/api";
import { Banner, Button, Dialog } from "@/ds";
import { Icon } from "@/lib";
import { useLang, useT } from "../../i18n";
import styles from "./request-activity-dialog.module.css";

export function RequestActivityDialog({ requestId, onClose, finalFocusRef }: {
  requestId: string;
  onClose: () => void;
  finalFocusRef: React.RefObject<HTMLElement | null>;
}) {
  const t = useT(), { lang } = useLang();
  const [page, setPage] = React.useState<RequestActivityPage | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);
  const controller = React.useRef<AbortController | null>(null);
  const busy = React.useRef(false);
  const initialFocusRef = React.useRef<HTMLDivElement | null>(null);
  const load = React.useCallback(async (cursor?: string) => {
    const signal = controller.current?.signal;
    if (!signal || signal.aborted || busy.current) return;
    busy.current = true;
    setLoading(true);
    setFailed(false);
    try {
      const result = await cockpit.getRequestActivity(requestId, cursor, signal);
      if (signal.aborted) return;
      setPage(previous => {
        const existing = cursor ? previous?.events ?? [] : [];
        const ids = new Set(existing.map(event => event.id));
        return { events: [...existing, ...result.events.filter(event => !ids.has(event.id))], next_cursor: result.next_cursor };
      });
    } catch (error) {
      if (signal.aborted) return;
      if (error instanceof ApiError && [401, 403, 404].includes(error.status)) setPage(null);
      setFailed(true);
    } finally {
      if (!signal.aborted) { busy.current = false; setLoading(false); }
    }
  }, [requestId]);
  React.useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    busy.current = false;
    void load();
    return () => current.abort();
  }, [load]);
  const dateFormat = new Intl.DateTimeFormat(lang === "de" ? "de-DE" : "en-GB", {
    dateStyle: "medium", timeStyle: "long",
  });
  return <Dialog open title={t("activity.title")} icon={<Icon name="history" size={20} />}
    onClose={onClose} initialFocusRef={initialFocusRef} finalFocusRef={finalFocusRef}
    footer={<Button variant="ghost" onClick={onClose}>{t("common.close")}</Button>}>
    <div ref={initialFocusRef} tabIndex={-1} className={styles.content}>
      {failed && <Banner tone="danger" title={t("activity.error")}
        actions={<Button variant="ghost" disabled={loading} onClick={() => void load(page?.next_cursor ?? undefined)}>
          {t("activity.retry")}
        </Button>} />}
      {loading && <p role="status">{t("activity.loading")}</p>}
      {page && page.events.length === 0 && !failed && !loading && <p>{t("activity.empty")}</p>}
      <ol className={styles.list} aria-label={t("activity.title")} aria-busy={loading}>
        {page?.events.map(event => <li key={event.id} className={styles.entry}>
          <strong>{t(`activity.action.${event.action}`, undefined, t("activity.action.unknown"))}</strong>
          <div className={styles.meta}>
            <span>{event.actor.kind === "human" ? event.actor.name : t(`activity.actor.${event.actor.kind}`)}</span>
            <time dateTime={event.created_at}>{dateFormat.format(new Date(event.created_at))}</time>
          </div>
        </li>)}
      </ol>
      {page?.next_cursor && !failed && <Button variant="ghost" disabled={loading}
        onClick={() => void load(page.next_cursor!)}>{t("activity.more")}</Button>}
    </div>
  </Dialog>;
}
